import React, { useState, useEffect } from 'react';
import {connect} from 'react-redux';
import { Modal, ModalBody, ModalFooter, Input, FormGroup, Spinner } from 'reactstrap';
import styles from './CollectionModal.scss';
import RemoveCollection from './RemoveCollection';
import {
  getCollections,
  saveCollections,
  updateCollections,
  addPropertyToCollections,
  deleteFavourite,
  removePropertyFromAllCollection,
  removePropertyFromCollection,
} from '../../../actions';
import { propertyViewTracker } from '../../../lib/tracking';
import {breakpointMdHigh} from '../../../lib/config';
import PropTypes from 'prop-types';
import {getCollectionCardThumbnail} from '../../../lib/ImagesUtil';

const CreateCollectionInput = ({ collectionName, setCollectionName }) =>
  <div className={'d-flex w-100'}>
    <img
      className={styles.collectionImage}
      src={'/assets/images/collectionPlaceholder.jpg'}
      alt="Collection"
    />
    <input
      className={styles.createCollectionInput}
      key="collectionName"
      type={'text'} value={collectionName}
      onChange={(e) => setCollectionName(e.target.value)}
      placeholder={'e.g. apartments, townhouses'}
    />
  </div>;

const CollectionListItem = ({ collectionName, collectionImage, collectionCount, collectionUuid, onClick, checked }) => {
  const [isClicked, setIsClicked] = useState(false);

  const handleOnClick = () => {
    if(!isClicked) {
      onClick();
    }
    if(collectionName !== 'All saved properties') setIsClicked(true);
  };

  useEffect(() => {
    setIsClicked(false);
  }, [checked]);

  return(
    <div className={styles.collectionListItem} onClick={() => handleOnClick()} >
      <img
        className={styles.collectionImage}
        src={collectionImage}
        alt="Collection"
      />
      <div className={styles.collectionListDetails}>
        <p>{collectionName}</p>
        <span>{collectionCount} propert{collectionCount > 1 ? 'ies' : 'y'}</span>
      </div>
      <div>
        <FormGroup check inline>
          {!isClicked ?
            <Input type="checkbox" value={collectionUuid} checked={checked} role={'button'}/> :
            <Spinner className={styles.spinner}/>
          }
        </FormGroup>
      </div>
    </div>
  );
}


const CollectionModal = (props) => {
  const {
    showModal,
    toggleModal,
    modalMode = 'ADD',
    exitOnSave = false,
    saveCollections,
    updateCollections,
    addPropertyToCollections,
    collections,
    collectionUuid,
    collectionName = '',
    customCSS = {},
    onExitCallback = () => {},
    property = {},
    userFavourites,
    deleteFavourite,
    removePropertyFromAllCollection,
    removePropertyFromCollection,
    onOpened = () => {},
    onClosed = () => {},
  } = props;

  const [mode, setMode] = useState(modalMode);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [collectionNameInput, setCollectionNameInput] = useState(collectionName);
  const [openRemoveCollectionModal, setOpenRemoveCollectionModal] = useState(false)

  useEffect(() => {
    if(modalMode !== 'EDIT') {
      setCollectionNameInput('');
    }
  }, [showModal]);

  useEffect(() => {
    setIsMobile(window.innerWidth <= breakpointMdHigh);
  }, []);

  const handleToggleModal = () => {
    setMode(modalMode);
    setError(null);
    toggleModal();
  };

  const checkPropertyExistsInCollection = (collectionUuid, othPropertyId) => {
    let hasMatch = false;
    collections.list.map(item => {
      if(item.collectionUuid === collectionUuid) {
        item.propertyIds.map(propertyId => {
          if(propertyId === othPropertyId) hasMatch = true;
        });
      }
    });

    return hasMatch;
  };

  const handleUpsertCollection = () => {
    const payload = { name: collectionNameInput, othPropertyIds: collections.othPropertyId ? [collections.othPropertyId] : []};

    if (collections.list.some(collection => collection.name === collectionNameInput.trim())) {
      setError('Collection name already exists');
      return;
    }

    if(mode === 'CREATE') {
      saveCollections(payload);
      propertyViewTracker('create new collection');
      propertyViewTracker('add property to collection', property);
    } else if(mode === 'EDIT') {
      payload.collectionUuid = collectionUuid;
      updateCollections(payload);
    }

    if(exitOnSave) {
      handleToggleModal();
      onExitCallback();
      return;
    };

    setMode('ADD');
    setCollectionNameInput('');
    setError(null);
  };

  const handleAddtoCollection = (collectionName, collectionUuid, othPropertyId) => {
    const payload = { collectionName, collectionUuid, othPropertyId };
    addPropertyToCollections(payload);
    propertyViewTracker('add property to collection', property);
  };

  const handleRemoveFromCollection = (collectionName, collectionUuid, othPropertyId) => {
    removePropertyFromCollection({
      collectionUuid: collectionUuid,
      collectionName: collectionName,
      othPropertyId: othPropertyId
    });
  };

  const handleRemoveFromAllCollection = () => {
    const collectionList = [];

    collections.list.map(collection => {
      collection.propertyIds.map(property => {
        if(property === collections.othPropertyId) {
          collectionList.push(collection.collectionUuid);
        }
      });
    });

    deleteFavourite({propertyId: collections.othPropertyId});

    removePropertyFromAllCollection({ collectionList: collectionList, propertyId: collections.othPropertyId });
    setOpenRemoveCollectionModal(false);
    handleToggleModal();
  };

  const collectionList = () =>
    <div className={'w-100'}>
      <CollectionListItem
        collectionUuid={''}
        collectionName={'All saved properties'}
        collectionImage={getCollectionCardThumbnail(userFavourites.favourites, 'default')}
        collectionCount={userFavourites.favourites.length}
        onClick={() => setOpenRemoveCollectionModal(true)}
        checked={true}
      />
      { collections.list.length > 0 ? collections.list.map((item, index) => {
        const isChecked = checkPropertyExistsInCollection(item.collectionUuid, collections.othPropertyId);
        return (
          <CollectionListItem
            key={index + item.collectionUuid}
            collectionUuid={item.collectionUuid}
            collectionImage={getCollectionCardThumbnail(userFavourites.favourites, 'collection', item.propertyIds)}
            collectionName={item.name}
            collectionCount={item.propertyCount}
            onClick={() => {
              if(!isChecked) {
                handleAddtoCollection(item.name, item.collectionUuid, collections.othPropertyId);
              } else {
                handleRemoveFromCollection(item.name, item.collectionUuid, collections.othPropertyId);
              }

            }}
            checked={isChecked}
          />
        );
      }
      ) :
        <p className={'mt-4 text-center'}>You currently have no custom lists</p>
      }
    </div>;

  const setTitle = () => {
    let title = '';
    switch (mode) {
      case 'ADD':
        title = 'Add to collection';
        break;
      case 'CREATE':
        title = 'Create a new collection';
        break;
      case 'EDIT':
        title = 'Edit collection name';
        break;
    }
    return title;
  };

  return (
    <>
      <Modal
        zIndex="7000"
        isOpen={showModal}
        toggle={() => handleToggleModal()}
        centered
        className={styles.addcreateModalContainer}
        modalClassName={styles.wrapClass}
        contentClassName={styles.modalContainer}
        style={!isMobile ? customCSS : {}}
        onOpened={onOpened}
        onClosed={onClosed}
      >
        <ModalBody className={styles.modalBody} style={{ paddingBottom: isMobile && mode === 'CREATE' ? 25 : 'inherit'}}>
          <div className={styles.modalHead}>
            <button
              className={styles.actionButton + ' ' + styles.actionButtonCancel}
              onClick={() => {
                if(isMobile){
                  if(mode === 'CREATE') {
                    setMode('ADD');
                  } else {
                    handleToggleModal();
                  }
                }
              }}
            />
            <div className={styles.modalTitle}>
              <span>{setTitle()}</span>
            </div>
            <button
              className={styles.actionButton + ' ' + styles.actionButtonDone}
              onClick={() => {
                if (isMobile) {
                  if (mode === 'CREATE' || mode === 'EDIT') {
                    handleUpsertCollection();
                  } else {
                    handleToggleModal();
                  }
                } else {
                  handleToggleModal();
                }
              }}
            />

          </div>
          <div className={styles.modalContent} id="body">
            { mode === 'CREATE' || mode === 'EDIT' ?
              <CreateCollectionInput collectionName={collectionNameInput} setCollectionName={setCollectionNameInput}/>
              :
              collectionList()
            }
          </div>
          {error ? <p className={styles.errorMessage}>{error}</p> : null}
        </ModalBody>
        <ModalFooter className={mode === 'ADD' ? styles.footerAddMode : styles.footerCreateMode}>
          <div className={'d-flex w-100 pt-3 pb-3 justify-content-center '}>
            { mode === 'EDIT' &&
              <button className={'odinButton pl-4 pr-4 w-auto mr-2 ' + styles.cancelButton} onClick={handleToggleModal}>
                Cancel
              </button>
            }
            { mode === 'CREATE' || mode === 'EDIT' ?
              <button className={'odinButton pl-4 pr-4 w-auto'} disabled={collectionNameInput === ''} onClick={() => handleUpsertCollection()} >
                Save
              </button>
              :
              <button className={styles.createCollectionButton} onClick={() => setMode('CREATE')}>
                Create new collection
              </button>
            }
          </div>
        </ModalFooter>
      </Modal>
      <RemoveCollection
        showModal={openRemoveCollectionModal}
        toggleModal={() => setOpenRemoveCollectionModal(false)}
        isFromCollectionModal={true}
        onRemoveFromAllProperties={() => handleRemoveFromAllCollection()}
      />
    </>
  );

};

CollectionModal.propTypes = {
  showModal: PropTypes.bool,
  toggleModal: PropTypes.func,
  modalMode: PropTypes.string,
  exitOnSave: PropTypes.bool,
  saveCollections: PropTypes.func,
  updateCollections: PropTypes.func,
  addPropertyToCollections: PropTypes.func,
  collections: PropTypes.object,
  collectionUuid: PropTypes.string,
  collectionName: PropTypes.string,
  customCSS: PropTypes.object,
  onExitCallback: PropTypes.func,
  property: PropTypes.object,
  userFavourites: PropTypes.object,
  deleteFavourite: PropTypes.func,
  removePropertyFromAllCollection: PropTypes.func,
  removePropertyFromCollection: PropTypes.func,
  onOpened: PropTypes.func,
  onClosed: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    userFavourites: state.userFavourites,
    collections: state.collections,
  };
};

const mapDispatchToProps = {
  saveCollections,
  updateCollections,
  addPropertyToCollections,
  deleteFavourite,
  removePropertyFromAllCollection,
  getCollections,
  removePropertyFromCollection,
};



export default connect(mapStateToProps, mapDispatchToProps)(CollectionModal);
